import type { CSSProperties } from 'react'
import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useQuery } from '@tanstack/react-query'

import type { PhotosApi } from '@jotta/types/PhotosApi'
import { CollectionType } from '@jotta/types/schemas/AlbumSchema'
import Spinner from '@jotta/ui/Spinner'
import SvgFillPlayPreview from '@jotta/ui/SvgFillPlayPreview'
import { useBreakpointIndex } from '@jotta/ui/useBreakpointIndex'
import { cn } from '@jotta/utils/css'

import type {
  CollectionPhotoCount,
  CollectionRef,
} from '../../../api/useCollection'
import { useCollectionPhotoCount } from '../../../api/useCollection'
import type { AlbumInfoStore } from '../../../store/AlbumInfoStore'

import { getAlbumInfoQueryOptions } from './AlbumInfo.loaders'
import { AlbumMembers } from './AlbumMembers'
import { AlbumMeta } from './AlbumMeta'
import { AlbumTitle } from './AlbumTitle'
import { SharedPhotosInfo } from './SharedPhotosInfo'
import { useAlbumTitleAction } from './useAlbumTitleAction'

function VideoOverlay({
  photo,
  style,
}: {
  photo: PhotosApi.Photo | undefined
  style?: CSSProperties
}) {
  return (
    photo?.duration && (
      <SvgFillPlayPreview
        className="pointer-events-none absolute left-1/2 top-1/2 h-1/4 -translate-x-1/2 -translate-y-1/2"
        style={style}
      />
    )
  )
}

function getAlbumMeta(
  store: AlbumInfoStore,
  photoCount?: CollectionPhotoCount,
) {
  // Keep the existing store-backed value until CountCollection resolves so
  // the header count does not flicker during the initial album load.
  if (!photoCount) {
    const fallbackPhotoCount = Math.max(
      store.model.total ?? 0,
      store.photoIds.length,
    )

    return {
      hasMorePhotos: store.album.hasMorePhotos,
      photoCount: fallbackPhotoCount,
    }
  }

  return photoCount.isExact
    ? {
        hasMorePhotos: false,
        photoCount: photoCount.count,
      }
    : {
        hasMorePhotos: true,
        photoCount: Math.max(photoCount.count, store.photoIds.length),
      }
}

export const AlbumInfo = observer<{
  albumIdOrShareId: string
  collectionRef: CollectionRef
  store: AlbumInfoStore
  goBig: boolean
}>(function AlbumInfo({ albumIdOrShareId, collectionRef, store, goBig }) {
  const [coverLoaded, setCoverLoaded] = useState(false)
  const [coverFailed, setCoverFailed] = useState(false)
  const { model: album } = store
  const isPublicAlbum = collectionRef.ref?.case === 'shareId'
  const { data: albumInfo } = useQuery(
    getAlbumInfoQueryOptions(
      {
        albumId: albumIdOrShareId,
        isPublic: isPublicAlbum,
      },
      {
        enabled: Boolean(albumIdOrShareId),
      },
    ),
  )
  const coverPhoto = albumInfo?.coverPhoto ?? album.coverPhoto
  const title = albumInfo?.title ?? album.title
  const { data: photoCount } = useCollectionPhotoCount(collectionRef)
  const albumMeta = getAlbumMeta(store, photoCount)
  const coverPhotoThumbnailUrl = coverPhoto?.thumbnail_url
    ? `${coverPhoto.thumbnail_url}.l`
    : undefined
  const largeView = useBreakpointIndex() > 1
  const isSharedPhotos = album.collectionType === CollectionType.SHARED_PHOTOS

  const { canChangeTitle, changeTitle, isPending } = useAlbumTitleAction({
    albumIdOrShareId,
    enabled: !isSharedPhotos,
  })

  useEffect(() => {
    setCoverLoaded(false)
    setCoverFailed(false)
  }, [coverPhotoThumbnailUrl])

  const content = isSharedPhotos ? (
    <SharedPhotosInfo
      testid={isPublicAlbum ? 'PublicAlbumView' : 'AlbumView'}
      store={store}
      goBig={goBig}
      hasMorePhotos={albumMeta.hasMorePhotos}
      captureDate={store.captureDate}
      photoCount={albumMeta.photoCount}
    />
  ) : (
    <div
      data-testid={isPublicAlbum ? 'PublicAlbumView' : 'AlbumView'}
      className="grid grid-cols-1 items-center gap-4 md:grid-cols-[400px_auto] md:gap-8"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg">
        <div
          className={cn(
            'size-full rounded-lg object-cover opacity-0 transition-opacity duration-500 ease-out',
            {
              'opacity-100': coverLoaded,
            },
          )}
        >
          {coverPhotoThumbnailUrl && !coverFailed && (
            <img
              className="size-full rounded-lg object-cover"
              src={coverPhotoThumbnailUrl}
              onLoad={() => setCoverLoaded(true)}
              onError={() => setCoverFailed(true)}
              alt="Album cover"
            />
          )}
          <VideoOverlay photo={coverPhoto} />
        </div>

        {!coverLoaded && !coverFailed && coverPhotoThumbnailUrl && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/10">
            <Spinner className="stroke-surface-container-lowest" />
          </div>
        )}
      </div>

      <div className="flex w-fit flex-col items-start">
        <div className="flex flex-col gap-1">
          <AlbumTitle
            title={title}
            canEdit={canChangeTitle}
            onSave={changeTitle}
            isPending={isPending}
          />
          {largeView && (
            <AlbumMeta
              hasMorePhotos={albumMeta.hasMorePhotos}
              captureDate={store.captureDate}
              photoCount={albumMeta.photoCount}
            />
          )}
        </div>
        {albumIdOrShareId && (
          <div className="mt-5">
            <AlbumMembers
              albumIdOrShareId={albumIdOrShareId}
              collectionType={album.collectionType}
            />
          </div>
        )}
      </div>
    </div>
  )

  return content
})
